/**
 * El QR de una factura recibida, leído EN EL NAVEGADOR — `OD-0054 D-08`
 * («Base sin IA», elegida por el propietario el 2026-10-22).
 *
 * La foto no sale del dispositivo para leerla: se pinta en un lienzo y `jsQR`
 * (versión FIJADA en `package.json`) busca el código en sus píxeles. Nada de
 * reconocimiento de texto ni de servicios externos. Se carga solo cuando hace
 * falta (`import()`), para que quien no anota compras no lo descargue.
 *
 * Lo que sale de aquí es el TEXTO del QR y una propuesta de campos. Si es o
 * no un QR de la AEAT lo decide la API (`NOT_AN_AEAT_QR`), y lo que manda es
 * lo que el usuario deja escrito: el QR solo avisa si no coincide.
 */

/** Lado máximo del lienzo: una foto de 12 Mpx no hace falta entera para encontrar un QR. */
const LADO_MAXIMO = 1600;

/** El texto del QR de una foto, o `null` si no se encuentra ninguno. Un PDF no se lee aquí. */
export async function leerQrDeImagen(fichero: Blob): Promise<string | null> {
  if (!fichero.type.startsWith("image/")) return null;
  let imagen: ImageBitmap;
  try {
    imagen = await createImageBitmap(fichero);
  } catch {
    return null;
  }
  const escala = Math.min(1, LADO_MAXIMO / Math.max(imagen.width, imagen.height));
  const ancho = Math.max(1, Math.round(imagen.width * escala));
  const alto = Math.max(1, Math.round(imagen.height * escala));
  const lienzo = document.createElement("canvas");
  lienzo.width = ancho;
  lienzo.height = alto;
  const ctx = lienzo.getContext("2d", { willReadFrequently: true });
  if (ctx === null) return null;
  ctx.drawImage(imagen, 0, 0, ancho, alto);
  imagen.close();
  const pixeles = ctx.getImageData(0, 0, ancho, alto);
  const modulo = await import("jsqr");
  // `jsqr` se publica como CommonJS: según el empaquetador llega en `default` o tal cual.
  const jsQR = (modulo.default ?? modulo) as typeof modulo.default;
  return jsQR(pixeles.data, ancho, alto, { inversionAttempts: "attemptBoth" })?.data ?? null;
}

/** El cotejo de la AEAT (`TDS-FISCAL` Parte 2 §3.1). Lo mismo comprueba la API. */
const SITIOS_AEAT = ["www2.agenciatributaria.gob.es", "prewww2.aeat.es"];
const RUTAS_AEAT = ["/wlpl/TIKE-CONT/ValidarQR", "/wlpl/TIKE-CONT/ValidarQRNoVerifactu"];

export type DatosDelQr = {
  readonly url: string;
  readonly nif: string | null;
  readonly numero: string | null;
  /** AAAA-MM-DD. En el QR llega como DD-MM-AAAA (`AEAT-REF-010` §6). */
  readonly fecha: string | null;
  /** El total tal cual, con la coma española: "363,00". Texto, no se suma nada. */
  readonly total: string | null;
};

/**
 * Lo que el QR propone para el formulario, SOLO si es un QR de la AEAT: uno de
 * otro sitio se ignora (`D-08`). El total no se escribe en ninguna parte: la
 * factura se anota por líneas, y la API avisa si su suma no da la del QR.
 */
export function datosDelQr(texto: string): DatosDelQr | null {
  let u: URL;
  try {
    u = new URL(texto.trim());
  } catch {
    return null;
  }
  if (u.protocol !== "https:" || !SITIOS_AEAT.includes(u.hostname) || !RUTAS_AEAT.includes(u.pathname)) return null;
  const f = u.searchParams.get("fecha")?.match(/^(\d{2})-(\d{2})-(\d{4})$/) ?? null;
  const importe = u.searchParams.get("importe");
  return {
    url: u.toString(),
    nif: u.searchParams.get("nif"),
    numero: u.searchParams.get("numserie"),
    fecha: f === null ? null : `${f[3]}-${f[2]}-${f[1]}`,
    total: importe !== null && /^-?\d{1,12}(\.\d{1,2})?$/.test(importe) ? importe.replace(".", ",") : null,
  };
}
